import FacebookLogin, {
  FacebookLoginClient,
} from '@greatsumini/react-facebook-login';
import * as Sentry from '@sentry/browser';
import LegacySemanticUiIcon from 'components/common/LegacySemanticUiIcon/LegacySemanticUiIcon';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useRouter } from 'next/router';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { isAndroid } from 'react-device-detect';
import { authApiClient } from 'utils/apiClient';
import { FACEBOOK_APP_ID } from 'utils/constants';
import { keepQueryParameter } from 'utils/url/keepQueryParameter';

import SocialLoginButton from './SocialLoginButton';
import FacebookIcon16 from './svg/16x16/FacebookIcon16.svg';

interface FacebookCompletionProps {
  accessToken: string;
  userID: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface FacebookProps {
  buttonText?: string;
  bgColor?: string;
  bgHoverColor?: string;
  completionHandler: (props: FacebookCompletionProps) => Promise<void>;
  scope?: string;
  className?: string;
  style?: CSSProperties;
  socialButtonStyle?: 'DEFAULT' | 'CIRCLE_ON_MOBILE' | undefined;
  /** Renders a compact button suited to sitting alongside Google/Apple in a single row, used by the 2026 late entry flow */
  enterFlow2026?: boolean;
}

const Facebook = ({
  buttonText = 'Login With Facebook',
  bgColor = '#3A5794',
  bgHoverColor = '#2A3E6A',
  completionHandler,
  scope,
  className,
  style,
  socialButtonStyle,
  enterFlow2026,
}: FacebookProps) => {
  const router = useRouter();
  const uiStore = useUIStore();
  const [loading, setLoading] = useState<boolean>(false);
  const isRedirected = useRef<boolean>(false);

  const dialogProps = {
    redirect_uri:
      typeof window !== 'undefined'
        ? keepQueryParameter(
            location.origin + location.pathname + location.search,
            'redirect',
          )
        : '/',
    state: 'facebookdirect',
    response_type: 'code',
    client_id: FACEBOOK_APP_ID,
  };

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    isRedirected.current = FacebookLoginClient.isRedirected(dialogProps);

    if (isRedirected.current) {
      setLoading(true);
      const loadProfile = async () => {
        try {
          const codeQueryStringParam = router.query.code;
          const code = Array.isArray(codeQueryStringParam)
            ? codeQueryStringParam[0]
            : codeQueryStringParam;
          if (!code) {
            console.error(
              'Failed to log in with facebook - ' + window.location.href,
            );
            uiStore.displayToast({
              title:
                'Sorry, there was a problem logging in with Facebook - Facebook did not return a log in code',
              color: '#FF6600',
              timeout: 9000,
            });
            return;
          }

          const profileResponse =
            await authApiClient.public.getFacebookProfileFromCode({
              body: {
                code: code,
                redirect_uri: dialogProps.redirect_uri,
              },
            });

          if (!('message' in profileResponse.body)) {
            completionHandler({
              accessToken: profileResponse.body.accessToken,
              userID: profileResponse.body.id,
              email: profileResponse.body.email,
              first_name: profileResponse.body.firstName,
              last_name: profileResponse.body.lastName,
            });
          } else {
            uiStore.displayToast({
              title: 'Sorry, there was a problem logging in with Facebook',
              color: '#FF6600',
              timeout: 9000,
            });
            console.error(
              'Facebook redirect login failed: ',
              profileResponse.body.message,
            );
          }
        } finally {
          setLoading(false);
        }
      };

      loadProfile();
    }
  }, [router.isReady]);

  const content = (
    <>
      <FacebookLogin
        scope={scope}
        appId={FACEBOOK_APP_ID}
        useRedirect={isAndroid ? true : undefined}
        dialogParams={dialogProps}
        onSuccess={(response) => {
          if (isRedirected.current) {
            console.log('Facebook login onSucccess aborted: redirected');
            return;
          }
          setLoading(false);

          FacebookLoginClient.getProfile(
            (res: any) => {
              completionHandler({
                accessToken: response.accessToken,
                userID: response.userID,
                email: res.email,
                first_name: res.first_name,
                last_name: res.last_name,
              });
            },
            { fields: 'id,email,first_name,last_name' },
          );
        }}
        onFail={(error) => {
          setLoading(false);
          if (error.status === 'facebookNotLoaded') {
            uiStore.displayToast({
              title:
                'There was a problem opening the Facebook login page. Please disable any adblockers and then reload the page.',
              color: '#FF6600',
              timeout: 9000,
            });
            console.log(
              'Facebook Login Failed due to facebookNotLoaded',
              error,
            );
            return;
          }
          if (error.status === 'loginCancelled') {
            console.log('Facebook Login was cancelled by user', error);
            return;
          }
          console.log('Facebook Login Failed!', error);
          Sentry.captureException(error);
        }}
        render={({ onClick }) => {
          const doLogin = () => {
            setLoading(true);
            onClick?.();
          };

          if (enterFlow2026) {
            return (
              <SocialLoginButton
                enterFlow2026="facebookStyle"
                imageSrcOrNode={<FacebookIcon16 />}
                loading={loading}
                text="Facebook"
                onClick={doLogin}
              />
            );
          }

          if (socialButtonStyle === 'CIRCLE_ON_MOBILE') {
            return (
              <SocialLoginButton
                imageSrcOrNode="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/enter/SocialFacebook.png"
                loading={loading}
                text="Facebook"
                onClick={doLogin}
              />
            );
          }

          return (
            <RoundedButton
              className={`continueWithFacebookButton ${className}`}
              hoverColor="#3A5794"
              hoverTextColor="white"
              style={{ ...style }}
              onClick={doLogin}
              loading={loading}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LegacySemanticUiIcon
                  name="facebook square"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '1.8em',
                    marginRight: '1em',
                  }}
                />
                {buttonText}
              </div>
            </RoundedButton>
          );
        }}
      />

      <style jsx>
        {`
          :global(.container .continueWithFacebookButton) {
            background-color: ${bgColor};
            width: 100%;
            max-width: 500px;
            box-shadow:
              rgb(0 0 0 / 24%) 0px 2px 2px 0px,
              rgb(0 0 0 / 24%) 0px 0px 1px 0px;
          }

          :global(.container .continueWithFacebookButton:hover) {
            background-color: ${bgHoverColor};
          }
        `}
      </style>
    </>
  );

  return enterFlow2026 ? (
    content
  ) : (
    <div
      className="container facebookLoginContainer"
      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
    >
      {content}
    </div>
  );
};

export default Facebook;
